// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { xScreen } from "@awc/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    cartState: getCurrentCartState(state),
    accounts: state.pages.rfqRequestQuote.accounts,
});

type Props = ReturnType<typeof mapStateToProps> & WidgetProps;

export interface RfqRequestQuotePageContainerStyles {
    spinnerWrapper?: InjectableCss;
    container?: InjectableCss;
    cartLinesContainer?: InjectableCss;
    specificationsContainer?: InjectableCss;
    emptyQuoteWrapper?: InjectableCss;
    emptyQuoteText?: TypographyPresentationProps;
}

export const rfqRequestQuotePageContainerStyles: RfqRequestQuotePageContainerStyles = {
    spinnerWrapper: {
        css: css`
            display: flex;
            justify-content: center;
            align-items: center;
            height: 300px;
        `,
    },
    emptyQuoteText: {
        variant: "h5",
    },
    container: {
        css: css`
            /* stylelint-disable value-no-vendor-prefix */
            display: -ms-grid;
            display: grid;
            padding: 1.5rem 0 0 1.5rem;
            margin: -1.5rem;
            overflow: hidden;

            ${xScreen("gt", "xs")} {
                grid-template-columns: minmax(0, 1fr);
                -ms-grid-columns: minmax(0, 1fr);
            }

            ${xScreen("gte", "md")} {
                grid-template-columns: repeat(2, minmax(0, 1fr));
                -ms-grid-columns: minmax(0, 1fr) minmax(0, 1fr);
                grid-template-rows: repeat(5, auto);
                -ms-grid-rows: auto auto auto auto auto;
            }

            ${xScreen("gt", "lg")} {
                grid-template-columns: minmax(0, 2fr) minmax(0, 1fr);
                -ms-grid-columns: minmax(0, 2fr) minmax(0, 1fr);
            }
        `,
    },
    cartLinesContainer: {
        css: css`
            ${xScreen("gt", "xs")} {
                border: 0 solid transparent;
                border-width: 0 1.5rem 1.5rem 0;
            }

            ${xScreen("gte", "md")} {
                grid-column-start: 1;
                -ms-grid-column: 1;
                grid-row-start: 1;
                -ms-grid-row: 1;
                grid-row-end: 6;
                -ms-grid-row-span: 6;
            }
        `,
    },
    specificationsContainer: {
        css: css`
            ${xScreen("gt", "xs")} {
                border: 0 solid transparent;
                border-width: 0 1.5rem 1.5rem 0;
            }

            ${xScreen("gte", "md")} {
                grid-column-start: 2;
                -ms-grid-column: 2;
                grid-row-start: 1;
                -ms-grid-row: 1;
            }
        `,
    },
};

const styles = rfqRequestQuotePageContainerStyles;

const RfqRequestQuotePageContainer = ({ cartState, accounts, id }: Props) => {
    // XNG-Changes: prevent duplicate spinners on page load (the other spinner is in RfqRequestQuotePage.tsx)
    if (!cartState.value || !accounts) {
        // return (
        // <StyledWrapper {...styles.spinnerWrapper}>
        //     <LoadingSpinner />
        // </StyledWrapper>
        // );
        return null;
    }

    if (!cartState.isLoading && cartState.value.lineCount === 0) {
        return (
            <StyledWrapper {...styles.emptyQuoteWrapper}>
                <Typography {...styles.emptyQuoteText} data-test-selector="requestQuoteCartIsEmpty">
                    {siteMessage("Rfq_EmptyQuoteMessage")}
                </Typography>
            </StyledWrapper>
        );
    }

    return (
        <StyledWrapper {...styles.container}>
            <StyledWrapper {...styles.specificationsContainer}>
                <Zone zoneName="Content1" contentId={id} />
            </StyledWrapper>
            <StyledWrapper {...styles.cartLinesContainer}>
                <Zone zoneName="Content0" contentId={id} />
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(RfqRequestQuotePageContainer),
    definition: {
        group: "RFQ Request Quote",
        displayName: "Page Container",
        allowedContexts: ["RfqRequestQuotePage"],
        fieldDefinitions: [],
    },
};

export default widgetModule;
